/**
 * Neyra — local mock "AI" description generator.
 * Composes a relevant, natural-sounding paragraph from the service inputs.
 * Pure offline; no network calls.
 */

const TONE_HOOKS = [
    "Trusted by clients across Lagos",
    "Built on years of hands-on experience",
    "A premium, detail-first approach",
    "Designed around your comfort and time",
    "Crafted for everyday excellence",
];

const CATEGORY_FLAVOR = {
    "Beauty & Personal Care": "looks that turn heads, with finishes that last",
    "Fashion & Tailoring": "perfect-fit pieces tailored to your style",
    "Home Repair & Skilled Trades": "reliable repairs done right the first time",
    "Cleaning & Domestic Services": "spotless results and a calmer home",
    "Logistics & Delivery": "fast, tracked, and dependable delivery",
    "Tech & Digital Services": "modern, fast, and rock-solid technology",
    "Events & Entertainment": "unforgettable moments, executed seamlessly",
    "Automotive Services": "your ride, kept in top condition",
    "Health & Wellness": "wellness routines that actually fit your life",
    "Business & Professional": "professional support that moves the needle",
    "Security Services": "real safety, day and night",
    "Education & Training": "skills you'll actually use, taught clearly",
};

const TYPE_LINE = {
    online: "Sessions are delivered fully online, so you can book from anywhere.",
    in_person: "I work in person, traveling to you or hosting at my studio.",
    hybrid: "Choose between in-person or online — whatever fits you best.",
};

const PRICE_LINE = {
    fixed: (c, p) => `Transparent flat pricing at ${c}${Number(p).toLocaleString()} — no surprises.`,
    from:  (c, p) => `Bookings start from ${c}${Number(p).toLocaleString()}, scaled to your needs.`,
    none:  () => "Reach out for a quick, personalized quote.",
};

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

export const generateServiceDescription = ({
    name,
    category,
    subcategory,
    serviceType = "in_person",
    pricingType = "from",
    price = 0,
    location = "Lagos, Nigeria",
    currency = "₦",
}) => {
    const focus = subcategory || category || "professional service";
    const flavor =
        CATEGORY_FLAVOR[category] ||
        "carefully delivered work you can count on";
    const opener = pick(TONE_HOOKS);
    const brand = name?.trim() ? `${name.trim()}` : "We";

    const lines = [
        `${brand} delivers ${focus.toLowerCase()} — ${flavor}.`,
        `${opener}, with a focus on quality, punctuality, and a smooth client experience.`,
        TYPE_LINE[serviceType],
        PRICE_LINE[pricingType] ? PRICE_LINE[pricingType](currency, price) : "",
        `Available across ${location}. Send a message to book or ask any question.`,
    ].filter(Boolean);

    return lines.join(" ");
};

const EVENT_HOOKS = [
    "Get ready for an unforgettable night",
    "Mark your calendar for one of Lagos's best evenings",
    "Join the community for a curated experience",
    "Step into a vibe built around great people, music, and energy",
    "A standout event you'll be glad you attended",
];

const EVENT_CATEGORY_FLAVOR = {
    "Music & Nightlife": "live sets, premium sound, and an electric crowd",
    "Food & Drinks":     "hand-picked menus and unforgettable tasting moments",
    "Sports & Fitness":  "energy, friendly competition, and a strong community",
    "Arts & Culture":    "thoughtful programming and inspiring conversations",
    "Tech & Business":   "actionable insights, sharp speakers, and quality networking",
    "Wellness & Lifestyle": "calm, intentional space designed to recharge you",
    "Networking & Social":  "the right room of people, made effortless to meet",
    "Outdoor & Adventure":  "fresh air, real movement, and great company",
};

export const generateEventDescription = ({
    title,
    category,
    subcategory,
    location = "Lagos, Nigeria",
    isFree = true,
    price = 0,
    currency = "₦",
    date,
}) => {
    const focus = subcategory || category || "experience";
    const flavor =
        EVENT_CATEGORY_FLAVOR[category] ||
        "thoughtfully curated programming you'll remember";
    const opener = pick(EVENT_HOOKS);
    const name = title?.trim() ? title.trim() : "This event";
    const priceLine = isFree
        ? "Entry is free — RSVP early to secure your spot."
        : `Tickets are ${currency}${Number(price || 0).toLocaleString()} and limited.`;
    const dateLine = date ? `Happening on ${date}.` : "";

    return [
        `${name} brings you ${focus.toLowerCase()} — ${flavor}.`,
        `${opener}, hosted in ${location}.`,
        dateLine,
        priceLine,
        "Tap Join to lock in your spot — see you there.",
    ].filter(Boolean).join(" ");
};
